import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { GlassCard } from '../components/GlassCard';
import { Crown, Users, MessageSquare, FileText, Send, ChevronLeft, ChevronRight, Sparkles, Lock } from 'lucide-react';
import mascotImg from '../assets/humanoid_original_download.png';

const ROLE_ACCENTS = {
  ceo:      ['#7C4DFF', '#5B2ED8'],
  cfo:      ['#0EA5B7', '#0284A3'],
  sales:    ['#F59E0B', '#B45309'],
  purchase: ['#10B981', '#047857'],  // retained for legacy weekly briefings
};
const ROLE_ICON = { ceo: '🧭', cfo: '💼', sales: '📈', purchase: '🧾' };
const ROLE_DEEPDIVE = {
  ceo: '/managers/reactive',
  cfo: '/managers/cfo',
  sales: '/sales-strategy',
};
const ROLE_INITIALS = { ceo: 'CEO', cfo: 'CFO', sales: 'SLS' };

export default function Boardroom() {
  const { token, hasPlan } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [locked, setLocked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('panel');

  const probeAccess = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      await api.boardroomThreads(token);
      setLocked(null);
    } catch (e) {
      const detail = e?.detail;
      const isGated = detail?.code === 'PLAN_REQUIRED' || detail?.error === 'premium_required';
      if (isGated) {
        setLocked({ current_plan: detail?.current_plan || 'free' });
      } else {
        toast.show(e?.message ?? 'Could not load Boardroom', 'error');
        setLocked({ current_plan: 'unknown' });
      }
    } finally { setLoading(false); }
  }, [token, toast]);

  useEffect(() => { probeAccess(); }, [probeAccess]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="boardroom-loading">
        <div className="w-8 h-8 border-2 border-[#7C4DFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (locked) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl mx-auto flex items-center justify-center min-h-[60vh]" data-testid="boardroom-locked">
        <GlassCard dark className="p-10 text-center max-w-md w-full">
          <img src={mascotImg} alt="Chairman" className="w-52 h-52 mx-auto mb-6 object-contain" data-testid="boardroom-locked-mascot" />
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
            <Crown size={14} className="text-amber-500" />
            <span className="text-amber-700 text-[11px] font-extrabold uppercase tracking-wide">Pro exclusive</span>
          </div>
          <h2 className="text-white font-extrabold text-xl leading-snug mb-3" data-testid="boardroom-locked-headline">
            Upgrade to Pro to hire your AI Management Team
          </h2>
          <p className="text-white/50 text-sm leading-relaxed mb-6">
            A multi-agent panel of AI Executives — CEO, CFO, Sales Head and Purchase Head — debates your toughest questions, plus a weekly Chairman&apos;s briefing delivered every Monday morning.
          </p>
          <button
            onClick={() => navigate('/billing')}
            data-testid="boardroom-locked-upgrade"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#7C4DFF] text-white font-extrabold text-sm shadow-lg shadow-[#7C4DFF]/30 hover:brightness-110 transition-all"
          >
            <Sparkles size={16} /> Upgrade to Pro
          </button>
          <p className="text-white/30 text-xs mt-4">Current plan · {locked.current_plan}</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="boardroom-screen">
      {/* Chairman Hero */}
      <div className="flex flex-col items-center mb-6" data-testid="boardroom-chairman-hero">
        <div
          className="w-[156px] h-[156px] rounded-[32px] flex items-center justify-center overflow-hidden border border-[rgba(124,77,255,0.35)]"
          style={{
            backgroundColor: 'rgba(255,255,255,0.04)',
            boxShadow: '0 14px 28px rgba(124,77,255,0.35), 0 0 40px rgba(124,77,255,0.15)',
          }}
        >
          <img src={mascotImg} alt="Chairman" className="w-[148px] h-[148px] object-contain" data-testid="boardroom-chairman-mascot" />
        </div>
        <div className="flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full bg-yellow-500/[0.14] border border-yellow-500/40">
          <Sparkles size={12} className="text-yellow-300" strokeWidth={2.4} />
          <span className="text-amber-700 text-[11px] font-extrabold uppercase tracking-wide">The Chairman is presiding</span>
        </div>
        <p className="text-white/50 text-xs mt-1">3-member board · CEO · CFO · Sales</p>
        <div className="flex gap-2 mt-3" data-testid="boardroom-avatars-row">
          {['ceo', 'cfo', 'sales'].map(r => {
            const [from, to] = ROLE_ACCENTS[r];
            return (
              <div
                key={r}
                data-testid={`boardroom-avatar-${r}`}
                className="w-[54px] h-[54px] rounded-full flex items-center justify-center border-2"
                style={{ borderColor: from + '66', backgroundColor: from + '18', boxShadow: '0 4px 10px rgba(15,23,42,0.10)' }}
              >
                <span className="font-extrabold text-xs tracking-wide" style={{ color: to }}>{ROLE_INITIALS[r]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-6" data-testid="boardroom-tabs">
        {[
          { key: 'panel', label: 'The Panel', Icon: Users },
          { key: 'chat', label: 'Ask the Board', Icon: MessageSquare },
          { key: 'weekly', label: 'Weekly', Icon: FileText },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`boardroom-tab-${t.key}`}
            className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold transition-all ${
              tab === t.key
                ? 'bg-[#7C4DFF]/15 text-[#7C4DFF] border border-[#7C4DFF]/30'
                : 'bg-white/[0.04] text-white/50 hover:text-white/70'
            }`}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'panel' && <PanelView />}
      {tab === 'chat' && <ChatView />}
      {tab === 'weekly' && <WeeklyView />}
    </div>
  );
}

/* ═══════════ The Panel ═══════════ */
function PanelView() {
  const { token } = useAuth();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState(null);
  const suggestions = useMemo(() => [
    'Why is my profit falling?',
    'Should I increase prices this quarter?',
    'How do I fix my working capital?',
  ], []);

  const ask = async (question) => {
    if (!token || !question.trim()) return;
    setAsking(true);
    setResult(null);
    try {
      const r = await api.boardroomAsk(token, { question: question.trim(), mode: 'panel' });
      setResult(r);
    } catch (e) { toast.show(e?.message ?? 'The Board is unreachable', 'error'); }
    finally { setAsking(false); }
  };

  return (
    <div data-testid="boardroom-panel-view">
      {!result && (
        <div className="space-y-2 mb-4" data-testid="boardroom-suggestions">
          <p className="text-white font-extrabold text-base">Ask the Boardroom</p>
          {suggestions.map(s => (
            <button
              key={s}
              onClick={() => { setQ(s); ask(s); }}
              data-testid={`boardroom-suggest-${s.slice(0, 10)}`}
              className="block w-full text-left px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/10 text-white text-sm font-semibold hover:bg-white/[0.07] transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {result && (
        <div className="space-y-4" data-testid="boardroom-panel-result">
          <div className="p-4 rounded-2xl bg-[#7C4DFF]/10 border border-[#7C4DFF]/25">
            <p className="text-[#7C4DFF] text-[10px] font-extrabold uppercase tracking-wider">Question</p>
            <p className="text-white font-bold text-sm mt-1">{result.question}</p>
          </div>

          {(result.roles || []).map(r => (
            <RoleCard key={r.role} role={r} />
          ))}

          <div className="p-5 rounded-3xl bg-[#0F172A] shadow-xl">
            <p className="text-yellow-300 text-[10px] font-extrabold uppercase tracking-wider mb-2">Chairman&apos;s Consensus</p>
            <p className="text-white font-semibold text-sm leading-relaxed">{result.consensus}</p>
          </div>

          <div className="text-center">
            <button
              onClick={() => setResult(null)}
              data-testid="boardroom-new-question"
              className="px-5 py-2.5 rounded-full bg-[#7C4DFF]/10 border border-[#7C4DFF]/25 text-[#7C4DFF] text-xs font-extrabold hover:bg-[#7C4DFF]/20 transition-colors"
            >
              Ask another question
            </button>
          </div>
        </div>
      )}

      {/* Input bar */}
      <div className="flex gap-3 mt-4 border-t border-white/10 pt-4 items-end">
        <textarea
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Type your board question…"
          disabled={asking}
          rows={1}
          data-testid="boardroom-panel-input"
          className="flex-1 min-h-[44px] max-h-[120px] px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/10 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#7C4DFF]/40 resize-none"
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); q.trim() && ask(q.trim()); } }}
        />
        <button
          onClick={() => q.trim() && ask(q.trim())}
          disabled={asking || !q.trim()}
          data-testid="boardroom-panel-send"
          className="w-11 h-11 flex-shrink-0 rounded-full bg-[#7C4DFF] flex items-center justify-center text-white disabled:opacity-40 hover:brightness-110 transition-all"
        >
          {asking ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}

function RoleCard({ role }) {
  const navigate = useNavigate();
  const [accent] = ROLE_ACCENTS[role.role] || ['#7C4DFF'];
  const [, accentDeep] = ROLE_ACCENTS[role.role] || ['#7C4DFF', '#5B2ED8'];
  const deepDive = ROLE_DEEPDIVE[role.role];
  return (
    <GlassCard dark className="p-4 space-y-2" testId={`boardroom-role-${role.role}`}>
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-extrabold"
        style={{ backgroundColor: accent + '15', borderColor: accent + '35', color: accent }}
      >
        <span>{ROLE_ICON[role.role] || '•'}</span>
        <span>{role.name}</span>
      </div>
      <p className="text-white/90 text-sm leading-relaxed">{role.response}</p>
      {deepDive && (
        <button
          data-testid={`boardroom-role-deepdive-${role.role}`}
          onClick={() => navigate(deepDive)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-extrabold mt-1 hover:brightness-110 transition-all"
          style={{ backgroundColor: accent + '18', borderColor: accent + '55', color: accentDeep }}
        >
          Open Deep Dive
          <ChevronRight size={14} strokeWidth={2.5} />
        </button>
      )}
    </GlassCard>
  );
}

/* ═══════════ Ask the Board (chat) ═══════════ */
function ChatView() {
  const { token } = useAuth();
  const toast = useToast();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadThreads = useCallback(async () => {
    if (!token) return;
    try {
      const r = await api.boardroomThreads(token);
      setThreads(r.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = async (id) => {
    if (!token) return;
    setActiveId(id);
    try {
      const r = await api.boardroomThread(token, id);
      setMessages(r.messages || []);
    } catch (e) { toast.show(e?.message ?? 'Could not load thread', 'error'); }
  };

  const send = async () => {
    if (!token || !input.trim()) return;
    const q = input.trim();
    setInput('');
    setSending(true);
    const cmid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setMessages(prev => [...prev, { id: `tmp-${cmid}`, role: 'user', content: q, created_at: new Date().toISOString() }]);
    try {
      const r = await api.boardroomAsk(token, {
        question: q, mode: 'chat',
        thread_id: activeId || undefined,
        client_message_id: cmid,
      });
      if (!activeId && r.thread_id) {
        setActiveId(r.thread_id);
        loadThreads();
      }
      setMessages(prev => [...prev, {
        id: `board-${cmid}`, role: 'board',
        content: r.consensus, meta: { roles: r.roles },
        created_at: new Date().toISOString(),
      }]);
    } catch (e) { toast.show(e?.message ?? 'The Board is unreachable', 'error'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#7C4DFF] border-t-transparent rounded-full animate-spin" /></div>;

  // No active thread — show thread list or empty state + composer
  if (!activeId) {
    return (
      <div data-testid="boardroom-chat-view">
        {threads.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-white/40 text-sm mb-4">Start a new board conversation.</p>
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            <p className="text-white font-extrabold text-sm">Your conversations</p>
            {threads.map(t => (
              <button
                key={t.id}
                onClick={() => openThread(t.id)}
                data-testid={`boardroom-thread-${t.id}`}
                className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] transition-colors"
              >
                <span className="text-white font-bold text-sm truncate flex-1 text-left">{t.title}</span>
                <span className="text-white/30 text-xs ml-2 flex-shrink-0">{(t.updated_at || '').slice(0, 10)}</span>
              </button>
            ))}
          </div>
        )}
        <ChatComposer input={input} setInput={setInput} onSend={send} sending={sending} />
      </div>
    );
  }

  // Active thread messages
  return (
    <div className="flex flex-col" data-testid="boardroom-chat-thread">
      <button onClick={() => setActiveId(null)} data-testid="boardroom-chat-back"
        className="inline-flex items-center gap-1 text-white/70 text-xs font-bold mb-3 hover:text-white transition-colors self-start">
        <ChevronLeft size={14} /> Back to conversations
      </button>
      <div className="space-y-3 mb-4 flex-1">
        {messages.map(m => (
          m.role === 'user' ? (
            <div key={m.id} className="ml-auto max-w-[80%] px-4 py-3 rounded-2xl rounded-tr-md bg-[#7C4DFF] text-white text-sm font-semibold leading-relaxed">
              {m.content}
            </div>
          ) : (
            <div key={m.id} className="mr-auto max-w-[90%] p-4 rounded-2xl rounded-tl-md bg-white/[0.04] border border-white/10">
              <p className="text-[#7C4DFF] text-[10px] font-extrabold uppercase tracking-wider mb-1">Chairman&apos;s Consensus</p>
              <p className="text-white font-semibold text-sm leading-relaxed">{m.content}</p>
              {(m.meta?.roles || []).map(r => (
                <div key={r.role} className="mt-3 pt-2 border-t border-white/10">
                  <p className="text-[#7C4DFF] text-[11px] font-extrabold">{r.name}</p>
                  <p className="text-white/60 text-xs mt-0.5 leading-relaxed line-clamp-4">{r.response}</p>
                </div>
              ))}
            </div>
          )
        ))}
      </div>
      <ChatComposer input={input} setInput={setInput} onSend={send} sending={sending} />
    </div>
  );
}

function ChatComposer({ input, setInput, onSend, sending }) {
  return (
    <div className="flex gap-3 border-t border-white/10 pt-4 items-end">
      <textarea
        value={input}
        onChange={e => setInput(e.target.value)}
        placeholder="Ask the Board anything…"
        disabled={sending}
        rows={1}
        data-testid="boardroom-chat-input"
        className="flex-1 min-h-[44px] max-h-[120px] px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/10 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#7C4DFF]/40 resize-none"
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
      />
      <button
        onClick={onSend}
        disabled={sending || !input.trim()}
        data-testid="boardroom-chat-send"
        className="w-11 h-11 flex-shrink-0 rounded-full bg-[#7C4DFF] flex items-center justify-center text-white disabled:opacity-40 hover:brightness-110 transition-all"
      >
        {sending ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send size={16} />}
      </button>
    </div>
  );
}

/* ═══════════ Weekly Briefing ═══════════ */
function WeeklyView() {
  const { token, hasPlan } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [detail, setDetail] = useState(null);
  const [canRunManual, setCanRunManual] = useState(true);
  const isPremium = hasPlan('premium');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await api.boardroomBriefings(token);
      setItems(r.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    if (!token) return;
    setRunning(true);
    try {
      await api.boardroomBriefingsRun(token);
      toast.show('Weekly briefing generated', 'success');
      load();
    } catch (e) {
      const code = e?.detail?.code;
      if (code === 'PLAN_REQUIRED') {
        setCanRunManual(false);
        toast.show('Manual runs are a Premium feature', 'error');
      } else {
        toast.show(e?.message ?? 'Could not run briefing', 'error');
      }
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="boardroom-weekly-view">
      {canRunManual && isPremium && (
        <div className="flex items-center justify-between px-4 py-3 rounded-2xl bg-white/[0.04] border border-white/10 mb-4">
          <span className="text-white/50 text-xs font-semibold">Manual run available (Premium)</span>
          <button
            onClick={runNow}
            disabled={running}
            data-testid="boardroom-weekly-run"
            className="px-4 py-2 rounded-full bg-[#7C4DFF] text-white text-xs font-extrabold disabled:opacity-50 hover:brightness-110 transition-all"
          >
            {running ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Run now'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[#7C4DFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-white/40 text-sm text-center py-8">No briefings yet — the Board runs every Monday morning.</p>
      ) : (
        <div className="space-y-3">
          {items.map(b => (
            <GlassCard
              dark key={b.id}
              className="p-4 cursor-pointer hover:border-white/20 transition-colors"
              onClick={() => setDetail(b)}
              testId={`boardroom-briefing-${b.id}`}
            >
              <p className="text-white/40 text-[11px]">{b.week_key} · {b.week_start} → {b.week_end}</p>
              <p className="text-white font-extrabold text-sm mt-1 line-clamp-2">{b.headline}</p>
              {(b.top_recommendations || []).slice(0, 3).map((r, i) => (
                <p key={i} className="text-white/60 text-xs leading-relaxed mt-1">• {r}</p>
              ))}
              <p className="text-[#7C4DFF] text-xs font-bold mt-2">View details →</p>
            </GlassCard>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div
            className="relative bg-[#0B1220] border border-white/10 rounded-t-3xl sm:rounded-3xl max-w-2xl w-full mx-4 p-6 shadow-2xl max-h-[80vh] overflow-y-auto"
            data-testid="boardroom-briefing-detail"
          >
            <h3 className="text-white font-extrabold text-lg mb-1">{detail.week_key}</h3>
            <p className="text-white font-extrabold text-sm mb-4">{detail.headline}</p>
            <p className="text-white/80 text-sm leading-relaxed mb-4">{detail.full_text}</p>
            {(detail.roles || []).map(r => (
              <RoleCard key={r.role} role={r} />
            ))}
            <div className="text-center mt-4">
              <button
                onClick={() => setDetail(null)}
                data-testid="boardroom-briefing-close"
                className="px-5 py-2.5 rounded-full bg-[#7C4DFF]/10 border border-[#7C4DFF]/25 text-[#7C4DFF] text-xs font-extrabold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
